import { Receipt } from 'lucide-react'
import { useMemo } from 'react'
import { Navigate, useLocation } from 'react-router'

import { BranchingSwitcher, type BranchingVariantOption } from '@/components/branching-switcher'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { useProfile } from '@/stores/profile'

import { FamilyBalancePage } from './billing/family-balance'
import { KitchenPayoutsPage } from './billing/kitchen'
import { SchoolARPage } from './billing/school'
import { Placeholder } from './placeholder'

/**
 * Billing — branching page. Variants:
 *   - family-balance: parent with any kid on BALANCE_LOAD_UP MealPlan
 *                     (or with no enrollments). Wallet UI + topup.
 *   - family-invoice: parent with any kid on INVOICE_NET_* MealPlan.
 *   - school: school admin sees organization-side AR.
 *   - kitchen: chef admin sees payouts.
 *
 * Final variant resolution is driven by the user's MealPlanEnrollment
 * rows + their fundingModel — fetched in a follow-on PR. For now we
 * stub by profileCode only.
 *
 * Per Plan 03's release-coupling rule, Families never use the invoice
 * variant in v1 — NET-30 institutional invoicing routes through the
 * School AR variant (Plan 14) instead. We surface `family-balance`
 * for any PARENT and reserve `family-invoice` for the future without
 * shipping the visible toggle entry.
 */

type BillingSwitcherKey = 'family' | 'school' | 'kitchen'

interface BillingResolution {
  /** The keys that drive the switcher (one fragment per visible panel). */
  switcherKeys: BillingSwitcherKey[]
  /** Whether to surface the family-invoice panel under /billing/family. */
  hasInvoiceLane: boolean
}

function resolveBilling(profileCodes: string[], isOrdoStaff: boolean): BillingResolution {
  const out: BillingSwitcherKey[] = []
  const isOrdoAdmin = isOrdoStaff
  if (profileCodes.includes('PARENT') || profileCodes.includes('TEACHER')) {
    out.push('family')
  }
  if (isOrdoAdmin || profileCodes.includes('SCHOOL_ADMIN')) {
    out.push('school')
  }
  if (isOrdoAdmin || profileCodes.includes('CHEF_ADMIN') || profileCodes.includes('SUPPLIER_ADMIN')) {
    out.push('kitchen')
  }
  return { switcherKeys: out, hasInvoiceLane: false }
}

function useUrlBilling(): BillingSwitcherKey | null {
  const { pathname } = useLocation()
  if (pathname.endsWith('/billing/family')) {
    return 'family'
  }
  if (pathname.endsWith('/billing/family-balance')) {
    return 'family'
  }
  if (pathname.endsWith('/billing/school')) {
    return 'school'
  }
  if (pathname.endsWith('/billing/kitchen')) {
    return 'kitchen'
  }
  return null
}

export function BillingDefaultRedirect(): React.ReactElement | null {
  const { profile, loading } = useProfile()
  // Keep the query: emailed links land on `/billing?addPaymentMethod=1`.
  const { search } = useLocation()
  if (loading || !profile) {
    return null
  }
  const { switcherKeys } = resolveBilling(profile.profileCodes, profile.isOrdoStaff)
  if (switcherKeys.length === 0) {
    return (
      <OrdoPageContainer>
        <Placeholder
          icon={Receipt}
          title="Billing"
          summary="Nothing to bill yet. Check your email if you were just invited."
        />
      </OrdoPageContainer>
    )
  }
  return <Navigate to={{ pathname: `/billing/${switcherKeys[0]!}`, search }} replace />
}

export function BillingPage() {
  const { profile } = useProfile()
  const urlVariant = useUrlBilling()
  const { switcherKeys } = useMemo(
    () => resolveBilling(profile?.profileCodes ?? [], profile?.isOrdoStaff ?? false),
    [profile?.profileCodes, profile?.isOrdoStaff],
  )

  if (urlVariant && !switcherKeys.includes(urlVariant)) {
    if (switcherKeys[0]) {
      return <Navigate to={`/billing/${switcherKeys[0]}`} replace />
    }
  }

  const show = (v: BillingSwitcherKey): boolean => {
    if (urlVariant) {
      return urlVariant === v
    }
    return switcherKeys.includes(v)
  }

  const switcherVariants: BranchingVariantOption[] = switcherKeys.map((v) => ({
    urlFragment: v,
    key: v,
    surface: v,
  }))

  return (
    <OrdoPageContainer>
      <BranchingSwitcher kind="billing" variants={switcherVariants} />
      <OrdoPageHeader icon={Receipt} title="Billing" />

      {show('family') ? <FamilyBalancePage /> : null}
      {show('school') ? <SchoolARPage /> : null}
      {show('kitchen') ? <KitchenPayoutsPage /> : null}
    </OrdoPageContainer>
  )
}
